import { Quaternion, TransformNode, Vector3, type AbstractMesh, type Scene } from "babylonjs";
import { CreatorFile, CreatorSceneChild } from "./types";

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

//----------------------------------------------------------------
// coordinate conversion (creator/Unity-space ↔ Babylon), shared by every element
// type. The element's anchor carries the full transform (position + rotation +
// scale), so it is the single editable handle the editor's gizmo attaches to.

export function convertPosition(p: [number, number, number]): Vector3 {
  return new Vector3(p[0], p[1], -p[2]); // invert Z. self-inverse.
}

export function convertScale(s: [number, number, number]): Vector3 {
  return new Vector3(s[0], s[1], s[2]); // identity.
}

// data rotation (degrees, Unity-space) → Babylon quaternion. all axes inverted;
// flat panels get a 180deg Y flip so the textured face points at the viewer.
// applied via an XYZ-order quaternion (matches the editor; correct for multi-axis
// rotations where Babylon's YXZ node.rotation would diverge).
export function rotationToQuaternion(r: [number, number, number], panelFlip: boolean): Quaternion {
  return eulerXYZToQuaternion(
    -r[0] * DEG2RAD,
    -r[1] * DEG2RAD + (panelFlip ? Math.PI : 0),
    -r[2] * DEG2RAD,
  );
}

// inverse of rotationToQuaternion: Babylon quaternion → data rotation (degrees).
export function quaternionToRotation(q: Quaternion, panelFlip: boolean): [number, number, number] {
  const e = quaternionToEulerXYZ(q); // radians, XYZ order.
  const flip = panelFlip ? Math.PI : 0;
  return [-e.x * RAD2DEG, (flip - e.y) * RAD2DEG, -e.z * RAD2DEG];
}

// build a quaternion from XYZ-order Euler angles (radians), matching the editor's
// quaternionFromEulerDegreesXYZ.
export function eulerXYZToQuaternion(rx: number, ry: number, rz: number): Quaternion {
  const c1 = Math.cos(rx / 2), s1 = Math.sin(rx / 2);
  const c2 = Math.cos(ry / 2), s2 = Math.sin(ry / 2);
  const c3 = Math.cos(rz / 2), s3 = Math.sin(rz / 2);
  return new Quaternion(
    s1 * c2 * c3 + c1 * s2 * s3,
    c1 * s2 * c3 - s1 * c2 * s3,
    c1 * c2 * s3 + s1 * s2 * c3,
    c1 * c2 * c3 - s1 * s2 * s3,
  );
}

// inverse of eulerXYZToQuaternion: quaternion → XYZ-order Euler (radians).
export function quaternionToEulerXYZ(q: Quaternion): Vector3 {
  const { x, y, z, w } = q;
  const m11 = 1 - 2 * (y * y + z * z);
  const m12 = 2 * (x * y - z * w);
  const m13 = 2 * (x * z + y * w);
  const m22 = 1 - 2 * (x * x + z * z);
  const m23 = 2 * (y * z - x * w);
  const m32 = 2 * (y * z + x * w);
  const m33 = 1 - 2 * (x * x + y * y);
  let ex: number, ez: number;
  const ey = Math.asin(Math.max(-1, Math.min(1, m13)));
  if (Math.abs(m13) < 0.9999999) {
    ex = Math.atan2(-m23, m33);
    ez = Math.atan2(-m12, m11);
  } else {
    ex = Math.atan2(m32, m22);
    ez = 0;
  }
  return new Vector3(ex, ey, ez);
}

//----------------------------------------------------------------
// one scene child (image/video/glb). owns a single anchor TransformNode — the
// transform handle the editor's gizmo attaches to — with its visual built
// underneath. subclasses implement build() (load + create the visual); the base
// owns the transform (apply/read), enabling read-back for the editor.
export abstract class CreatorElement {
  readonly sceneID: string;
  readonly assetId: string;
  readonly anchor: TransformNode;

  protected scene: Scene;
  protected child: CreatorSceneChild;
  protected file: CreatorFile;

  constructor(scene: Scene, parent: TransformNode, child: CreatorSceneChild, file: CreatorFile) {
    this.scene = scene;
    this.child = child;
    this.file = file;
    this.sceneID = child.sceneID;
    this.assetId = child.assetID;
    this.anchor = new TransformNode(`creator_${child.sceneID}`, scene);
    this.anchor.parent = parent;
  }

  // load the asset + create the visual under the anchor (call applyTransform()).
  abstract build(): Promise<void>;

  // flat panels (image/video) get a 180deg Y flip; models do not.
  protected get panelFlip(): boolean {
    return true;
  }

  // apply this element's data transform to the anchor.
  protected applyTransform(): void {
    this.anchor.position.copyFrom(convertPosition(this.child.position));
    this.anchor.rotationQuaternion = rotationToQuaternion(this.child.rotation, this.panelFlip);
    this.anchor.scaling.copyFrom(convertScale(this.child.scale));
  }

  // read the anchor's current transform back to data (Unity) space — for the
  // editor's gizmo read-back / getSceneConfig.
  readTransform(): {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: [number, number, number];
  } {
    const p = this.anchor.position;
    const q = this.anchor.rotationQuaternion ?? Quaternion.FromEulerVector(this.anchor.rotation);
    const s = this.anchor.scaling;
    return {
      position: [p.x, p.y, -p.z],
      rotation: quaternionToRotation(q, this.panelFlip),
      scale: [s.x, s.y, s.z],
    };
  }

  // set the transform from data (Unity) space — for programmatic re-sync. The
  // gizmo manipulates the anchor directly; this is the data path. (The editor
  // subclass overrides the transform handling to work in its internal space.)
  setDataTransform(
    position: [number, number, number],
    rotation: [number, number, number],
    scale: [number, number, number],
  ): void {
    this.child = { ...this.child, position, rotation, scale };
    this.applyTransform();
  }

  // stamp the element id on the anchor + every built mesh so a pick anywhere on
  // the element resolves it (used by the editor's picker). Call after build().
  stampMetadata(): void {
    const metadata = { elementId: this.sceneID };
    this.anchor.metadata = metadata;
    for (const mesh of this.anchor.getChildMeshes(false)) mesh.metadata = metadata;
  }

  // the element's visual meshes (under the anchor) — for consumers that reveal /
  // measure the content (e.g. an AR app's fade-in + placement bounds).
  getMeshes(): AbstractMesh[] {
    return this.anchor.getChildMeshes(false);
  }

  // show/hide the whole element (subclasses extend, e.g. video pauses when hidden).
  setEnabled(visible: boolean): void {
    this.anchor.setEnabled(visible);
  }

  dispose(): void {
    this.anchor.dispose(false, true);
  }
}

export default CreatorElement;
